<template>
  <div class="chat-tour-overlay">
    <!-- ─────────────────────────────────────────────────────────────
         DUMMY REPLICA of the fullscreen Team Chat interface.
         Entirely static / driven by the current step's `state`.
         pointer-events disabled so nothing here is interactive — this
         is what "freezes" the UI during the tour.
    ────────────────────────────────────────────────────────────────── -->
    <div class="tour-replica">
      <!-- Top bar -->
      <div
        class="primary d-flex align-center justify-space-between px-2 tour-topbar"
      >
        <div class="d-flex align-center" style="min-width: 0;">
          <v-icon color="white" small class="mr-2">mdi-chat-outline</v-icon>
          <span
            class="white--text subtitle-2 font-weight-bold d-flex align-center"
            data-tour-key="switcher"
          >
            {{ state.dropdownOpen ? 'Study Dataset' : collectionName }}
            <v-icon small color="white" class="ml-1">mdi-chevron-down</v-icon>
          </span>
        </div>
        <div class="d-flex align-center">
          <v-icon small color="white" class="mr-2">mdi-arrow-collapse</v-icon>
          <v-icon small color="white">close</v-icon>
        </div>

        <!-- Study dataset dropdown (step 1) -->
        <div
          v-if="state.dropdownOpen"
          class="tour-dropdown"
          data-tour-key="dropdown"
        >
          <div class="tour-dropdown-search-input d-flex align-center px-3">
            <span class="grey--text">Select dataset…</span>
          </div>
          <div
            v-for="ds in datasetOptions"
            :key="ds"
            class="tour-dropdown-item"
            :class="{ 'tour-dropdown-item--active': ds === collectionName }"
          >
            {{ ds }}
          </div>
        </div>
      </div>

      <!-- Body: sidebar + message column -->
      <div class="tour-body">
        <!-- ── Channel sidebar ──────────────────────────────── -->
        <div class="tour-sidebar">
          <div class="tour-sidebar-scroll" data-tour-key="sidebar-list">
            <!-- Channels -->
            <div data-tour-key="channels-section">
              <div class="tour-section-header">
                <span>Channels</span>
                <v-icon x-small>{{
                  state.sections.channels
                    ? 'mdi-chevron-down'
                    : 'mdi-chevron-right'
                }}</v-icon>
              </div>
              <div v-if="state.sections.channels">
                <div
                  class="tour-channel-item"
                  :class="{
                    'tour-channel-item--active': activeChannel === 'general',
                  }"
                  data-tour-key="general-channel"
                >
                  # general
                </div>
                <div
                  v-if="state.showCustomChannel"
                  class="tour-channel-item"
                  data-tour-key="custom-channel"
                >
                  # Hypotheses
                </div>
              </div>
            </div>

            <!-- Study groups -->
            <div data-tour-key="cohorts-section">
              <v-divider></v-divider>
              <div class="tour-section-header">
                <span>Study groups</span>
                <v-icon x-small>{{
                  state.sections.cohorts
                    ? 'mdi-chevron-down'
                    : 'mdi-chevron-right'
                }}</v-icon>
              </div>
              <div v-if="state.sections.cohorts">
                <div
                  v-for="c in cohortChannels"
                  :key="c"
                  class="tour-channel-item"
                >
                  {{ c }}
                </div>
              </div>
            </div>

            <!-- Analyses -->
            <div data-tour-key="analyses-section">
              <v-divider></v-divider>
              <div class="tour-section-header">
                <span>Analyses</span>
                <v-icon x-small>{{
                  state.sections.analyses
                    ? 'mdi-chevron-down'
                    : 'mdi-chevron-right'
                }}</v-icon>
              </div>
              <div v-if="state.sections.analyses">
                <div
                  v-for="a in analysisChannels"
                  :key="a"
                  class="tour-channel-item"
                >
                  {{ a }}
                </div>
              </div>
            </div>

            <!-- Direct Messages -->
            <div data-tour-key="dms-section">
              <v-divider></v-divider>
              <div class="tour-section-header">
                <span style="flex: 1;">Direct Messages</span>
                <span class="d-flex align-center">
                  <v-icon
                    x-small
                    class="tour-dm-add"
                    data-tour-key="new-groupdm-btn"
                    >mdi-plus</v-icon
                  >
                  <v-icon x-small class="ml-1">{{
                    state.sections.dms
                      ? 'mdi-chevron-down'
                      : 'mdi-chevron-right'
                  }}</v-icon>
                </span>
              </div>
              <div v-if="state.sections.dms">
                <div v-for="d in dmChannels" :key="d" class="tour-channel-item">
                  {{ d }}
                </div>
                <div v-if="state.showGroupDm" class="tour-channel-item">
                  {{ groupDmName }}
                </div>
              </div>
            </div>
          </div>
          <div class="tour-sidebar-foot">
            <span
              class="primary--text caption font-weight-medium"
              data-tour-key="new-channel-btn"
            >
              <v-icon x-small color="primary">add</v-icon> New Channel
            </span>
          </div>
        </div>

        <!-- ── Message column ───────────────────────────────── -->
        <div class="tour-msgcol">
          <!-- New group DM composer (step 6) -->
          <template v-if="state.dialog === 'newGroupDm'">
            <div class="tour-msg-header">
              <span class="subtitle-1 font-weight-bold primary--text"
                >New Conversation</span
              >
            </div>
            <div class="pa-4" data-tour-key="new-groupdm-composer">
              <div class="body-2 grey--text mb-3">
                Select one person for a direct message, or multiple people for a
                group conversation.
              </div>
              <div class="tour-input d-flex align-center flex-wrap">
                <span class="grey--text mr-2">To:</span>
                <span class="tour-chip">Sarah Chen</span>
                <span class="tour-chip">James Okafor</span>
              </div>
              <div class="mt-4">
                <span class="tour-btn tour-btn--primary">Open</span>
                <span class="tour-btn ml-2">Cancel</span>
              </div>
            </div>
          </template>

          <!-- Normal channel thread -->
          <template v-else>
            <div class="tour-msg-header">
              <span class="subtitle-1 font-weight-bold primary--text">
                # {{ activeChannel }}
                <v-icon small color="primary">mdi-chevron-down</v-icon>
              </span>
            </div>
            <div class="tour-msg-thread">
              <div
                v-for="(m, i) in messages"
                :key="i"
                class="tour-message"
                :class="{
                  'tour-message--active': state.showMsgActions && m.own,
                }"
              >
                <div class="tour-avatar">{{ m.initial }}</div>
                <div style="min-width: 0;">
                  <div class="tour-msg-meta d-flex align-center">
                    <span class="font-weight-bold">{{ m.user }}</span>
                    <span class="caption grey--text ml-2">{{ m.time }}</span>
                    <div
                      v-if="state.showMsgActions && m.own"
                      class="tour-msg-actions ml-auto d-flex align-center"
                    >
                      <v-icon x-small class="mr-2" data-tour-key="msg-edit"
                        >edit</v-icon
                      >
                      <v-icon x-small data-tour-key="msg-delete">delete</v-icon>
                    </div>
                  </div>
                  <!-- eslint-disable-next-line -->
                  <div class="tour-msg-content"><template v-for="(seg, si) in m.segments"><span v-if="seg.type === 'channel'" :key="si" class="tour-tag tour-tag--channel primary--text" :data-tour-key="seg.tourKey">{{ seg.text }}<v-icon v-if="seg.icon" x-small color="primary" class="tour-tag-icon">{{ seg.icon }}</v-icon></span><span v-else-if="seg.type === 'mention'" :key="si" class="tour-tag tour-tag--mention primary--text" :data-tour-key="seg.tourKey">{{ seg.text }}<v-icon v-if="seg.icon" x-small color="primary" class="tour-tag-icon">{{ seg.icon }}</v-icon></span><span v-else :key="si">{{ seg.text }}</span></template></div>
                  <img
                    v-if="m.image"
                    :src="m.image"
                    alt="comparison"
                    class="tour-attachment mt-1"
                  />
                </div>
              </div>
            </div>
            <div class="tour-composer" data-tour-key="composer">
              <!-- Mention / channel autocomplete picker -->
              <div
                v-if="state.picker"
                class="tour-picker"
                data-tour-key="picker"
              >
                <div
                  v-for="(it, pi) in state.picker.items"
                  :key="pi"
                  class="tour-picker-item d-flex align-center px-3"
                  :class="{
                    'tour-picker-item--active': pi === state.picker.highlight,
                  }"
                >
                  <v-icon small class="mr-2">{{ it.icon }}</v-icon>
                  <span class="caption"
                    >{{ state.picker.sigil }}{{ it.display }}</span
                  >
                </div>
              </div>
              <!-- Pending image preview -->
              <div
                v-if="state.pendingImage"
                class="tour-pending-image d-flex align-center pa-1 mb-1"
                data-tour-key="pending-image"
              >
                <img :src="state.pendingImage.src" class="tour-pending-thumb" />
                <span class="caption grey--text ml-2" style="flex: 1;">{{
                  state.pendingImage.name
                }}</span>
                <v-icon x-small>mdi-close</v-icon>
              </div>
              <div
                class="tour-textarea"
                :class="{ 'grey--text': !state.composerText }"
              >
                {{ state.composerText || 'Message # ' + activeChannel }}
              </div>
              <div class="d-flex justify-end align-center mt-1">
                <span class="caption grey--text mr-2">Enter to send</span>
                <v-icon small class="mr-2" data-tour-key="attach-btn"
                  >mdi-paperclip</v-icon
                >
                <span class="tour-btn tour-btn--primary">Send</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- New channel dialog (step 3) -->
      <div
        v-if="state.dialog === 'newChannel'"
        class="tour-modal-card"
        data-tour-key="new-channel-dialog"
      >
        <div class="subtitle-1 font-weight-bold mb-3">New Channel</div>
        <div class="tour-input mb-3">Hypotheses</div>
        <div class="tour-input tour-input--placeholder mb-3">
          Add members (optional)
        </div>
        <div class="d-flex justify-end">
          <span class="tour-btn">Cancel</span>
          <span class="tour-btn tour-btn--primary ml-2">Create</span>
        </div>
      </div>
    </div>

    <!-- ─────────────────────────────────────────────────────────────
         SPOTLIGHT + ARROWS overlay (SVG). pointer-events: none.
    ────────────────────────────────────────────────────────────────── -->
    <svg class="tour-svg" :width="vw" :height="vh">
      <defs>
        <mask id="tour-spotlight-mask">
          <rect :width="vw" :height="vh" fill="white" />
          <rect
            v-for="(t, i) in ringTargets"
            :key="'hole-' + i"
            :x="t.rect.x"
            :y="t.rect.y"
            :width="t.rect.w"
            :height="t.rect.h"
            rx="6"
            fill="black"
          />
        </mask>
        <marker
          id="tour-arrowhead"
          markerWidth="10"
          markerHeight="10"
          refX="7"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L7,3 L0,6 Z" fill="#ffb300" />
        </marker>
      </defs>

      <!-- Dim everything except the holes -->
      <rect
        :width="vw"
        :height="vh"
        :fill="targets.length ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.45)'"
        mask="url(#tour-spotlight-mask)"
      />

      <!-- Highlight border around each ring target -->
      <rect
        v-for="(t, i) in ringTargets"
        :key="'ring-' + i"
        :x="t.rect.x"
        :y="t.rect.y"
        :width="t.rect.w"
        :height="t.rect.h"
        rx="6"
        fill="none"
        stroke="#ffb300"
        stroke-width="2"
      />

      <!-- Arrows -->
      <g v-for="(a, i) in arrows" :key="'arrow-' + i">
        <line
          :x1="a.start.x"
          :y1="a.start.y"
          :x2="a.end.x"
          :y2="a.end.y"
          stroke="#ffb300"
          stroke-width="3"
          marker-end="url(#tour-arrowhead)"
        />
        <template v-if="a.label">
          <circle :cx="a.start.x" :cy="a.start.y" r="11" fill="#ffb300" />
          <text
            :x="a.start.x"
            :y="a.start.y + 4"
            text-anchor="middle"
            font-size="13"
            font-weight="700"
            fill="#000"
          >
            {{ a.label }}
          </text>
        </template>
      </g>
    </svg>

    <!-- ─────────────────────────────────────────────────────────────
         EXPLANATION CARD — the only interactive element.
    ────────────────────────────────────────────────────────────────── -->
    <v-card ref="card" class="tour-card" :style="cardStyle">
      <v-card-subtitle class="pb-0 pt-3 tour-card-step">
        Step {{ currentStepIndex + 1 }} of {{ steps.length }}
      </v-card-subtitle>
      <v-card-title class="primary--text subtitle-1 font-weight-bold py-1">
        {{ currentStep.title }}
      </v-card-title>
      <v-card-text class="pb-2 tour-card-body">
        {{ currentStep.body }}
        <div class="tour-dots mt-3">
          <span
            v-for="(s, i) in steps"
            :key="i"
            class="tour-dot"
            :class="{ 'tour-dot--active': i === currentStepIndex }"
          ></span>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn text x-small @click="exit">Exit tour</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          text
          small
          class="mr-1"
          :disabled="currentStepIndex === 0"
          @click="prev"
          >Back</v-btn
        >
        <v-btn color="primary" small @click="next">{{
          currentStepIndex === steps.length - 1 ? 'Done' : 'Next'
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
const CARD_WIDTH = 340;
const CARD_MARGIN = 24;
const ARROW_LEN = 64;
// The card's bottom edge is pinned at this fraction of the viewport height
// (measured from the bottom) so Back/Next never move — taller steps grow
// upward instead.
const CARD_BOTTOM_RATIO = 0.38;

export default {
  name: 'ChatTour',
  data() {
    return {
      currentStepIndex: 0,
      vw: window.innerWidth,
      vh: window.innerHeight,
      targets: [],
      cardRect: null,
      cardLeft: CARD_MARGIN,
      // Dummy data
      collectionName: 'Hearing Loss',
      datasetOptions: [
        'Hearing Loss',
        'NHANES AUX 2015-2020',
        'PPMI QUERY',
        'PRODROMAL-HEALTHY-PD',
      ],
      cohortChannels: [
        'Age 60+, low HbA1c',
        'Age 60+, high HbA1c',
        'Job loud noise - yes',
        'Job loud noise - no',
      ],
      analysisChannels: ['Analysis 1', 'Analysis 2', 'Analysis 3'],
      dmChannels: ['Sarah Chen', 'James Okafor', 'Maria Lopez'],
      groupDmName: 'Sarah, James, you',
      activeChannel: 'general',
      messages: [
        {
          user: 'Sarah Chen',
          initial: 'S',
          time: '9:02 AM',
          segments: [
            {
              type: 'text',
              text:
                "Welcome everyone! I've shared the Hearing Loss dataset with the team.",
            },
          ],
        },
        {
          user: 'You',
          initial: 'Y',
          time: '9:08 AM',
          own: true,
          segments: [
            { type: 'text', text: "Thanks Sarah! I'll post findings in " },
            {
              type: 'channel',
              text: '#Analysis 2',
              icon: 'mdi-chart-bar',
              tourKey: 'tag-hash',
            },
            { type: 'text', text: '.' },
          ],
        },
        {
          user: 'James Okafor',
          initial: 'J',
          time: '9:14 AM',
          segments: [
            { type: 'text', text: 'Big trend in ' },
            {
              type: 'mention',
              text: '@Analysis 2',
              icon: 'mdi-chart-bar',
              tourKey: 'tag-at-analysis',
            },
            { type: 'text', text: ' — nice work ' },
            {
              type: 'mention',
              text: '@Maria Lopez',
              icon: 'mdi-account',
              tourKey: 'tag-at-user',
            },
            { type: 'text', text: '!' },
          ],
        },
        {
          user: 'Maria Lopez',
          initial: 'M',
          time: '9:21 AM',
          segments: [{ type: 'text', text: "Here's the comparison plot:" }],
          image: '/images/tour-comparison.png',
        },
      ],
    };
  },
  computed: {
    steps() {
      return [
        {
          title: 'Welcome to Team Chat',
          body:
            'Team Chat lets you discuss a study dataset with everyone it’s shared with — organized into channels and direct messages. Here’s a quick tour.',
          targets: [],
          state: this.baseState(),
        },
        {
          title: 'Select a study dataset',
          body:
            'Start by choosing a study dataset from this menu. You can type in the search box to filter. Only datasets you’ve shared, or that have been shared with you, appear here.',
          targets: [
            {
              keys: ['switcher', 'dropdown'],
              arrow: false,
              pad: { l: 6, t: 4, r: 8, b: 8 },
            },
          ],
          state: { ...this.baseState(), dropdownOpen: true },
        },
        {
          title: 'Channels',
          body:
            'Channels organize conversations by topic. Every dataset starts with a General channel at the top for dataset-wide discussion. Below it you’ll find the other channel types.',
          targets: [
            { key: 'sidebar-list', arrow: false },
            { key: 'general-channel', ring: false },
          ],
          state: this.baseState(),
        },
        {
          title: 'Create a custom channel',
          body:
            'Need a focused space for a specific topic? Click New Channel ① and then name it and add members ②. Your custom channel will appear beneath General in the Channels list.',
          targets: [
            { key: 'new-channel-btn', label: '1', pad: { r: 12 } },
            { key: 'new-channel-dialog', label: '2' },
          ],
          state: { ...this.baseState(), dialog: 'newChannel' },
        },
        {
          title: 'Study group & analysis channels',
          body:
            'Channels are great for focusing a conversation on one part of a dataset — so a channel is created automatically for each study group and each saved analysis.',
          targets: [
            { keys: ['cohorts-section', 'analyses-section'], arrow: false },
            { key: 'cohorts-section', ring: false },
            { key: 'analyses-section', ring: false },
          ],
          state: {
            ...this.baseState(),
            showCustomChannel: true,
            sections: {
              channels: true,
              cohorts: true,
              analyses: true,
              dms: false,
            },
          },
        },
        {
          title: 'Direct messages',
          body:
            'Direct messages are private. A one-on-one channel is created automatically for each person the dataset is shared with.',
          targets: [{ key: 'dms-section' }],
          state: {
            ...this.baseState(),
            showCustomChannel: true,
            sections: {
              channels: false,
              cohorts: false,
              analyses: false,
              dms: true,
            },
          },
        },
        {
          title: 'Group direct messages',
          body:
            'To chat privately with several people at once, create a group DM. Click the + next to Direct Messages ① and pick who to include ②.',
          targets: [
            { key: 'new-groupdm-btn', label: '1', arrowFrom: 'bottom' },
            { key: 'new-groupdm-composer', label: '2' },
          ],
          state: {
            ...this.baseState(),
            showCustomChannel: true,
            dialog: 'newGroupDm',
            sections: {
              channels: false,
              cohorts: false,
              analyses: false,
              dms: true,
            },
          },
        },
        {
          title: 'Writing a message',
          body:
            'Type in the composer ① and press Enter — or click Send — to post. Note that to add new lines without posting, you can use Shift+Enter. Hovering over your own messages gives you the option to edit ② or delete ③ them.',
          targets: [
            { key: 'composer', label: '1' },
            { key: 'msg-edit', label: '2', arrowFrom: 'left' },
            { key: 'msg-delete', label: '3', arrowFrom: 'top' },
          ],
          state: {
            ...this.baseState(),
            showMsgActions: true,
            composerText:
              "Agreed — let's compare the two HbA1c groups directly.",
          },
        },
        {
          title: 'Creating links',
          body:
            'While writing, type @ or # to open the picker shown below. Keep typing to filter, then press Enter or click to drop in a link. @ finds people, study groups, and analyses; # finds channels.',
          targets: [{ key: 'picker' }],
          state: {
            ...this.baseState(),
            composerText: 'Great work everyone @',
            picker: {
              sigil: '@',
              highlight: 0,
              items: [
                { icon: 'mdi-account', display: 'Sarah Chen' },
                { icon: 'mdi-account', display: 'Maria Lopez' },
                { icon: 'mdi-account-group', display: 'Age 60+, high HbA1c' },
                { icon: 'mdi-chart-bar', display: 'Analysis 2' },
              ],
            },
          },
        },
        {
          title: 'Linking to people and items',
          body:
            'Use @ to mention a person or point to a study group or analysis. @mentioning a teammate ① notifies them and links to your direct message. An @study group or @analysis ② jumps straight to that item in the study group selector or the analysis page respectively.',
          targets: [
            { key: 'tag-at-user', label: '1' },
            { key: 'tag-at-analysis', label: '2' },
          ],
          state: this.baseState(),
        },
        {
          title: 'Linking to channels',
          body:
            '# links to a channel — General, a custom channel, or the channel automatically created for a study group or analysis. Clicking the link opens that channel right here in Team Chat. (So @Analysis 2 opens the analysis in the analysis page, while #Analysis 2 opens its channel.)',
          targets: [{ key: 'tag-hash' }],
          state: this.baseState(),
        },
        {
          title: 'Sharing an image',
          body:
            'Click the paperclip ① to attach an image — or paste or drag one straight into the composer. It shows as a preview ② before you send; add a caption if you like, then click Send to post it.',
          targets: [
            { key: 'attach-btn', label: '1', arrowFrom: 'top-left' },
            { key: 'pending-image', label: '2' },
          ],
          state: {
            ...this.baseState(),
            composerText: 'Comparison of the two HbA1c groups:',
            pendingImage: {
              src: '/images/tour-comparison.png',
              name: 'comparison.png',
            },
          },
        },
        {
          title: 'You’re all set',
          body:
            'That’s the layout of Team Chat. Select a shared study dataset to jump in — your channels and direct messages will be waiting.',
          targets: [],
          state: {
            ...this.baseState(),
            showCustomChannel: true,
            showGroupDm: true,
            sections: {
              channels: true,
              cohorts: true,
              analyses: true,
              dms: true,
            },
          },
        },
      ];
    },
    currentStep() {
      return this.steps[this.currentStepIndex];
    },
    state() {
      return this.currentStep.state;
    },
    cardStyle() {
      return {
        left: this.cardLeft + 'px',
        bottom: this.cardBottom + 'px',
        width: CARD_WIDTH + 'px',
      };
    },
    cardBottom() {
      return Math.round(this.vh * CARD_BOTTOM_RATIO);
    },
    ringTargets() {
      return this.targets.filter(t => t.ring);
    },
    arrows() {
      const out = [];
      this.targets
        .filter(t => t.arrow)
        .forEach(t => {
          // Arrow forced to approach the target from a specific side.
          if (t.arrowFrom) {
            out.push(this.sideArrow(t.rect, t.arrowFrom, t.label));
            return;
          }
          // Otherwise the arrow emanates from the explanation card.
          if (!this.cardRect) return;
          const from = {
            x: this.cardRect.x + this.cardRect.w / 2,
            y: this.cardRect.y + this.cardRect.h / 2,
          };
          const edge = this.rayRectIntersection(t.rect, from);
          let dx = from.x - edge.x;
          let dy = from.y - edge.y;
          const dist = Math.hypot(dx, dy) || 1;
          dx /= dist;
          dy /= dist;
          const end = { x: edge.x + dx * 8, y: edge.y + dy * 8 };
          const len = Math.min(ARROW_LEN, Math.max(20, dist - 16));
          const start = { x: end.x + dx * len, y: end.y + dy * len };
          out.push({ start, end, label: t.label });
        });
      return out;
    },
  },
  watch: {
    currentStepIndex() {
      this.measure();
    },
  },
  mounted() {
    window.addEventListener('resize', this.measure);
    window.addEventListener('keydown', this.onKeydown);
    this.measure();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    baseState() {
      return {
        dropdownOpen: false,
        dialog: null,
        showCustomChannel: false,
        showGroupDm: false,
        showMsgActions: false,
        composerText: '',
        picker: null,
        pendingImage: null,
        sections: {
          channels: true,
          cohorts: false,
          analyses: false,
          dms: false,
        },
      };
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.exit();
      else if (e.key === 'ArrowRight') this.next();
      else if (e.key === 'ArrowLeft') this.prev();
    },
    next() {
      if (this.currentStepIndex === this.steps.length - 1) this.exit();
      else this.currentStepIndex += 1;
    },
    prev() {
      if (this.currentStepIndex > 0) this.currentStepIndex -= 1;
    },
    exit() {
      this.$emit('close');
    },
    measure() {
      this.vw = window.innerWidth;
      this.vh = window.innerHeight;
      this.$nextTick(() => {
        const root = this.$el;
        if (!root) return;
        const ts = [];
        (this.currentStep.targets || []).forEach(t => {
          const keys = t.keys || (t.key ? [t.key] : []);
          let raw = null;
          keys.forEach(k => {
            const el = root.querySelector(`[data-tour-key="${k}"]`);
            if (el) {
              const r = el.getBoundingClientRect();
              const rr = { x: r.left, y: r.top, w: r.width, h: r.height };
              raw = raw ? this.unionRect(raw, rr) : rr;
            }
          });
          if (!raw) return;
          const pad = { l: 5, t: 5, r: 5, b: 5, ...(t.pad || {}) };
          ts.push({
            label: t.label || null,
            ring: t.ring !== false,
            arrow: t.arrow !== false,
            arrowFrom: t.arrowFrom || null,
            rect: {
              x: raw.x - pad.l,
              y: raw.y - pad.t,
              w: raw.w + pad.l + pad.r,
              h: raw.h + pad.t + pad.b,
            },
          });
        });
        this.targets = ts;
        // Place the card once we know its height, then re-measure its
        // final rect so the card-anchored arrows are accurate.
        this.$nextTick(() => {
          const cEl = this.cardEl();
          const cardH = cEl ? cEl.getBoundingClientRect().height : 160;
          this.cardLeft = this.computeCardLeft(cardH);
          this.$nextTick(() => {
            const el = this.cardEl();
            if (el) {
              const r = el.getBoundingClientRect();
              this.cardRect = { x: r.left, y: r.top, w: r.width, h: r.height };
            }
          });
        });
      });
    },
    cardEl() {
      const c = this.$refs.card;
      return c && (c.$el || c);
    },
    unionRect(a, b) {
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const right = Math.max(a.x + a.w, b.x + b.w);
      const bottom = Math.max(a.y + a.h, b.y + b.h);
      return { x, y, w: right - x, h: bottom - y };
    },
    overlapArea(a, b) {
      const xo = Math.max(
        0,
        Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)
      );
      const yo = Math.max(
        0,
        Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y)
      );
      return xo * yo;
    },
    computeCardLeft(cardH) {
      const W = CARD_WIDTH;
      const M = CARD_MARGIN;
      const top = this.vh - cardH - this.cardBottom;
      const boxes = this.targets.map(t => t.rect);
      const totalOverlap = left =>
        boxes.reduce(
          (s, b) =>
            s + this.overlapArea({ x: left, y: top, w: W, h: cardH }, b),
          0
        );
      // Prefer the center; only move if the centered card would cover a
      // highlighted target.
      const centered = Math.round((this.vw - W) / 2);
      if (totalOverlap(centered) === 0) return centered;
      const leftPos = M;
      const rightPos = this.vw - W - M;
      const lo = totalOverlap(leftPos);
      const ro = totalOverlap(rightPos);
      if (lo === 0 && ro === 0) {
        const avgX =
          boxes.reduce((s, b) => s + (b.x + b.w / 2), 0) / boxes.length;
        return avgX < this.vw / 2 ? rightPos : leftPos;
      }
      if (lo === 0) return leftPos;
      if (ro === 0) return rightPos;
      return lo <= ro ? leftPos : rightPos;
    },
    sideArrow(rect, side, label) {
      const mid = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
      let edge;
      let out;
      if (side === 'bottom') {
        edge = { x: mid.x, y: rect.y + rect.h };
        out = { x: 0, y: 1 };
      } else if (side === 'top') {
        edge = { x: mid.x, y: rect.y };
        out = { x: 0, y: -1 };
      } else if (side === 'left') {
        edge = { x: rect.x, y: mid.y };
        out = { x: -1, y: 0 };
      } else if (side === 'top-left') {
        // Approaches from the upper-left: starts to the left of the target
        // and a little above, pointing down-right into it.
        edge = { x: rect.x, y: mid.y };
        out = { x: -0.95, y: -0.31 };
      } else {
        edge = { x: rect.x + rect.w, y: mid.y };
        out = { x: 1, y: 0 };
      }
      const end = { x: edge.x + out.x * 8, y: edge.y + out.y * 8 };
      const start = {
        x: end.x + out.x * ARROW_LEN,
        y: end.y + out.y * ARROW_LEN,
      };
      return { start, end, label: label || null };
    },
    rayRectIntersection(rect, from) {
      const cx = rect.x + rect.w / 2;
      const cy = rect.y + rect.h / 2;
      const dx = from.x - cx;
      const dy = from.y - cy;
      if (dx === 0 && dy === 0) return { x: cx, y: cy };
      const hw = rect.w / 2;
      const hh = rect.h / 2;
      const scaleX = dx !== 0 ? hw / Math.abs(dx) : Infinity;
      const scaleY = dy !== 0 ? hh / Math.abs(dy) : Infinity;
      const scale = Math.min(scaleX, scaleY);
      return { x: cx + dx * scale, y: cy + dy * scale };
    },
  },
};
</script>

<style scoped>
.chat-tour-overlay {
  position: fixed;
  inset: 0;
  z-index: 2400;
  overflow: hidden;
}

/* ── Replica ─────────────────────────────────────────────── */
.tour-replica {
  position: absolute;
  inset: 0;
  background: white;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  user-select: none;
}
.tour-topbar {
  height: 40px;
  flex-shrink: 0;
  position: relative;
}
.tour-dropdown {
  position: absolute;
  top: 38px;
  left: 8px;
  width: 240px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  padding: 4px 0;
  z-index: 5;
}
.tour-dropdown-search-input {
  margin: 8px;
  height: 40px;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  font-size: 0.85rem;
}
.tour-dropdown-item {
  padding: 8px 16px;
  font-size: 0.85rem;
}
.tour-dropdown-item--active {
  background: #e3f2fd;
  font-weight: 600;
}

.tour-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.tour-sidebar {
  width: 220px;
  border-right: 1px solid #e0e0e0;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}
.tour-sidebar-scroll {
  flex: 1;
  overflow: hidden;
}
.tour-sidebar-foot {
  padding: 8px;
  border-top: 1px solid #e0e0e0;
}
.tour-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--v-primary-base, #3f51b5);
}
.tour-channel-item {
  padding: 4px 16px;
  font-size: 0.8rem;
  min-height: 32px;
  display: flex;
  align-items: center;
}
.tour-channel-item--active {
  background: #e3f2fd;
}
.tour-dm-add {
  border: 1px solid currentColor;
  border-radius: 50%;
}

.tour-msgcol {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.tour-msg-header {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
}
.tour-msg-thread {
  flex: 1;
  overflow: hidden;
  padding: 8px 0;
}
.tour-message {
  position: relative;
  padding: 4px 12px 4px 40px;
}
.tour-message--active {
  background: rgba(0, 0, 0, 0.08);
}
.tour-avatar {
  position: absolute;
  left: 12px;
  top: 5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--v-primary-base, #3f51b5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 600;
}
.tour-msg-meta {
  line-height: 1.2;
}
.tour-msg-actions {
  color: #616161;
}
.tour-msg-content {
  font-size: 0.9rem;
  white-space: pre-wrap;
}
.tour-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 500;
}
.tour-tag--channel {
  background: var(--tag-channel-bg, rgba(25, 118, 210, 0.12));
}
.tour-tag--mention {
  background: var(--tag-mention-bg, rgba(255, 193, 7, 0.12));
}
.tour-tag-icon {
  font-size: 11px !important;
}
.tour-attachment {
  display: block;
  max-width: 320px;
  max-height: 240px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.tour-composer {
  padding: 12px;
  border-top: 1px solid #e0e0e0;
  position: relative;
}
.tour-picker {
  position: absolute;
  bottom: 100%;
  left: 12px;
  width: 260px;
  margin-bottom: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
  z-index: 5;
}
.tour-picker-item {
  height: 34px;
}
.tour-picker-item--active {
  background: #e8f4fd;
}
.tour-pending-image {
  background: #f5f5f5;
  border-radius: 4px;
}
.tour-pending-thumb {
  max-height: 80px;
  max-width: 120px;
  border-radius: 4px;
  object-fit: cover;
}
.tour-textarea {
  border: 1px solid #c0c0c0;
  border-radius: 4px;
  padding: 10px;
  min-height: 52px;
  font-size: 0.9rem;
}

.tour-input {
  border: 1px solid #c0c0c0;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.9rem;
}
.tour-input--placeholder {
  color: #9e9e9e;
}
.tour-chip {
  background: #e0e0e0;
  border-radius: 12px;
  padding: 2px 10px;
  margin: 2px;
  font-size: 0.8rem;
}
.tour-btn {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--v-primary-base, #3f51b5);
}
.tour-btn--primary {
  background: var(--v-primary-base, #3f51b5);
  color: white;
}

.tour-modal-card {
  position: absolute;
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  width: 440px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  padding: 16px;
  z-index: 6;
}

/* ── Spotlight + arrows ─────────────────────────────────── */
.tour-svg {
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
}

/* ── Card ───────────────────────────────────────────────── */
.tour-card {
  position: fixed;
  z-index: 2500;
}
.tour-card-step {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
}
.tour-card-body {
  font-size: 0.9rem;
  line-height: 1.45;
}
.tour-dots {
  display: flex;
  gap: 5px;
}
.tour-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cfd2dc;
  transition: background 0.15s, transform 0.15s;
}
.tour-dot--active {
  background: var(--v-primary-base, #3f51b5);
  transform: scale(1.35);
}
</style>
